/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

// Note Reducer for notes in current course

import { Reducer, AnyAction } from "redux";
import { NoteEntryType } from "client/views/ClassroomPage/NoteEntry";

import {
  GET_LEC_NOTES,
  DELETE_NOTE,
  UPDATE_NOTE,
  EXPAND_NOTES,
  EXPAND_NOTE,
  GET_LECS_WITH_NOTES,
} from "client/actions/types";

// format of Store
//
//   curLecture: lecture with notes currently in use
//   'lid1': [{ _id, label, text, clr, vpos, seq }, { ... },... ]
//   'lid2': [{ _id, label, text, clr, vpos, seq }, { ... },... ]
//   ......
//   lecQueue: lecture queue. The most recently used one is at the end of the array.
//   lecsWithNotes: { target_course, [ lid_1, lid_2, ...] }

export type LecturesWithNotes = {
  course: string;
  lecList: string[];
};

export type NotesState = {
  readonly [key: string]: any;
  readonly curLecture: string;
  readonly lecQueue: string[];
  readonly lecsWithNotes: LecturesWithNotes | undefined;
};

const noteReducerDefaultState: NotesState = {
  curLecture: "",
  lecQueue: [],
  lecsWithNotes: undefined,
};

const queueSize = 6;

// Put a lecture in the queue.
// 1. If the lecture is not yet in the queue, add it. If the queue is full, remove the least used one.
// 2. If the lecture is already in the queue, move it to the end (which means most recently accessed).
const addQueue = (lecQueue: string[], lecture: string) => {
  let lecToRemove = {};

  const idx = lecQueue.indexOf(lecture);
  if (idx === -1) {
    // lecture is not in the queue, add it

    if (lecQueue.length === queueSize) {
      // if queue full, remove least accessed one
      lecToRemove = { [lecQueue[0]]: undefined };
      lecQueue.shift();
    }
    // put the most recently accessed to end of queue
    lecQueue.push(lecture);
  } else {
    // move the lecture to the end, as it's most recently accessed
    lecQueue.push(lecQueue.splice(idx, 1)[0]);
  }

  return { lecQueue, lecToRemove };
};

export const noteReducer: Reducer<NotesState, AnyAction> = (
  state = noteReducerDefaultState,
  action: AnyAction,
) => {
  switch (action.type) {
    case UPDATE_NOTE: {
      // console.log(">R> UPDATE: ", action.payload);
      const { lecture, noteId, note } = action.payload;

      if (!state[lecture]) {
        // It's the first note of this lecture. Add the lecture into queue.
        const { lecQueue, lecToRemove } = addQueue(state.lecQueue, lecture);

        const newState = {
          ...state,
          [lecture]: [note],
          lecQueue,
          ...lecToRemove,
        };
        // Add the lecture to list of "Lectures with Notes"
        if (state.lecsWithNotes) {
          const { course, lecList } = state.lecsWithNotes;
          lecList.push(lecture);
          newState.lecsWithNotes = { course, lecList };
        }
        return newState;
      } else {
        const lecNotes = state[lecture];
        const idx = lecNotes.findIndex(
          (nt: NoteEntryType) => nt._id === noteId,
        );
        if (idx === -1) {
          lecNotes.push(note); // a new note in this lecture
        } else {
          lecNotes[idx] = { ...lecNotes[idx], ...note };
        }
        return { ...state, [lecture]: lecNotes };
      }
    }

    case DELETE_NOTE: {
      // console.log(">R> DELETE: ", action.payload);
      const { lecture, noteId } = action.payload;

      const lecNotes = state[lecture];
      if (lecNotes === undefined) {
        return state; // this should not happen
      }

      const idx = lecNotes.findIndex((nt: NoteEntryType) => nt._id === noteId);
      if (idx !== -1) {
        lecNotes.splice(idx, 1);

        if (lecNotes.length === 0) {
          // if there's no note under this lecture, remove the lecture from queue
          const lecQueue = state.lecQueue;
          lecQueue.splice(lecQueue.indexOf(lecture), 1);
          const newState = { ...state, [lecture]: undefined, lecQueue };

          // Remove the lecture from list of "Lectures with Notes"
          if (state.lecsWithNotes) {
            const { course, lecList } = state.lecsWithNotes;
            const i = lecList.indexOf(lecture);
            if (i !== -1) lecList.splice(i, 1);
            newState.lecsWithNotes = { course, lecList };
          }
          return newState;
        } else {
          return { ...state, [lecture]: lecNotes };
        }
      } else {
        return state;
      }
    }

    case GET_LEC_NOTES: {
      // console.log(">R> GETNOTE: ", action.payload);
      const { lecture, notes } = action.payload;

      // Two things we need to do here:
      // 1. sort the notes by seq (creation sequence)
      // 2. adjust the lecture queue, if the note is "available" (see rules below)
      const notesAvailable =
        (!notes && state[lecture].length) || // notes is 'null' means it has been loaded. Stored notes is not [].
        (notes && notes.length !== 0); // notes read from DB, and not []

      if (notesAvailable) {
        let updatedNotes = {};
        if (notes) {
          notes.sort((n1: NoteEntryType, n2: NoteEntryType) => n1.seq - n2.seq);
          updatedNotes = { [lecture]: notes };
        }

        const { lecQueue, lecToRemove } = addQueue(state.lecQueue, lecture);
        return {
          ...state,
          lecQueue,
          curLecture: lecture,
          ...lecToRemove,
          ...updatedNotes,
        };
      } else {
        return { ...state, curLecture: lecture };
      }
    }

    case EXPAND_NOTES: {
      // console.log(">R> EXPAND_NOTES: ", action.payload);
      const { lecture, expanded } = action.payload;
      const notes = state[lecture];
      notes.forEach(
        (note: NoteEntryType, idx: number, notes: NoteEntryType[]) => {
          notes[idx].expanded = expanded;
        },
      );
      return {
        ...state,
        [lecture]: notes,
      };
    }

    case EXPAND_NOTE: {
      const { lecture, expanded, noteId } = action.payload;
      const notes = state[lecture];
      const idx = notes.findIndex((note: NoteEntryType) => note._id === noteId);
      if (idx !== -1) notes[idx].expanded = expanded;
      return {
        ...state,
        [lecture]: notes,
      };
    }

    case GET_LECS_WITH_NOTES: {
      return {
        ...state,
        lecsWithNotes: action.payload,
      };
    }

    default:
      return state;
  }
};
